import { useCallback, useEffect, useRef, useState } from "react";

export const MICROPHONE_TIMEOUT_MS = 12000;
export const CONNECTION_TIMEOUT_MS = 20000;

// Timing out cannot cancel getUserMedia or the SDK connect promise. Dispose
// late results and invalidate the attempt before allowing another connection.
export function createStartupAttempt(onStage) {
  let active = true;
  const cancellations = new Set();
  return {
    get active() { return active; },
    cancel() {
      active = false;
      cancellations.forEach((cancel) => cancel());
      cancellations.clear();
    },
    wait(stage, operation, timeoutMs, disposeLate = () => {}) {
      onStage(stage);
      return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (error, value) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          cancellations.delete(cancel);
          if (error) reject(error); else resolve(value);
        };
        const cancel = () => finish(Object.assign(new Error("Startup cancelled"), { code: "startup_cancelled", stage }));
        const timer = setTimeout(() => finish(Object.assign(new Error("Startup timed out"), { code: `${stage}_timeout`, stage })), timeoutMs);
        cancellations.add(cancel);
        let pending;
        try {
          if (!active) { cancel(); return; }
          pending = operation();
        } catch (error) {
          finish(Object.assign(new Error("Voice startup failed"), {
            code: error?.code || error?.name || "voice_startup_error", stage, cause: error,
          }));
          return;
        }
        Promise.resolve(pending).then((value) => {
          if (settled || !active) { disposeLate(value); return; }
          finish(null, value);
        }, (error) => finish(Object.assign(new Error("Voice startup failed"), {
          code: error?.code || error?.name || "voice_startup_error", stage, cause: error,
        })));
      });
    },
  };
}

// A lifecycle-neutral guard: callers retain their own session/reconnect rules.
export function useVoiceStartupAttempt() {
  const current = useRef(null);
  const [stage, setStage] = useState("idle");
  const [error, setError] = useState(null);
  const cancel = useCallback(() => {
    current.current?.cancel();
    current.current = null;
    setStage("idle");
    setError(null);
  }, []);
  const begin = useCallback(() => {
    current.current?.cancel();
    const attempt = createStartupAttempt(setStage);
    current.current = attempt;
    setError(null);
    return attempt;
  }, []);
  const fail = useCallback((failure) => {
    setStage("failed");
    setError({stage: failure.stage || "connecting_realtime", code: failure.code || failure.name || "voice_startup_error"});
  }, []);
  const ready = useCallback(() => setStage("ready"), []);
  useEffect(() => () => { current.current?.cancel(); }, []);
  return {begin, cancel, fail, ready, stage, error};
}
